import { useEffect, useRef, useState } from 'react'

export type UseDebouncedValueOptions<T> = {
  /**
   * Values that apply at once instead of on the delay, dropping any pending value with them. A
   * cleared search box is the case: there is no further keystroke to coalesce, and the term it
   * replaces must not come back for the rest of the window.
   */
  immediate?: (value: T) => boolean
}

export const useDebouncedValue = <T>(value: T, delay = 300, { immediate }: UseDebouncedValueOptions<T> = {}): T => {
  const [debounced, setDebounced] = useState(value)
  // Held in a ref so an inline predicate does not restart the timer on every render.
  const immediateRef = useRef(immediate)
  immediateRef.current = immediate

  useEffect(() => {
    if (immediateRef.current?.(value)) {
      setDebounced(value)
      return
    }
    const handle = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(handle)
  }, [value, delay])

  return immediate?.(value) ? value : debounced
}
